/**
 * Authoritative pagination/iteration field map for the redesigned REST API source UI.
 *
 * This is a 1:1 transcription of the EXISTING legacy `ConfigureAPIStage` behavior
 * (see the field inventory) — the redesign is usability-only, so every config key,
 * label, default, option set, and conditional must match the legacy form exactly.
 * The redesigned pagination popover renders these definitions; values are written
 * straight into `rest.iterations[stageIndex][field.key]` via `onAPIConfigChanged`.
 *
 * Selectable `iteration.type` values mirror `Constants.IterationTypes` (11 entries).
 * `static.url` (implicit when not paginated) and `data.map.key.queue` (managed via
 * source-level lookups) are intentionally NOT selectable here.
 */

export type IterationFieldType = 'text' | 'number' | 'checkbox' | 'select';

export interface IterationFieldOption {
  label: string;
  value: string;
}

export interface IterationField {
  /** config key written into rest.iterations[stageIndex] */
  key: string;
  label: string;
  type: IterationFieldType;
  /** marks "(Optional) ..." fields; matches legacy labels/behavior */
  optional?: boolean;
  /** default applied by DEFAULT_ITERATION / backend field defs (do not invent) */
  default?: string | number | boolean;
  placeholder?: string;
  /** for select fields (e.g. file.response.format) — hardcoded in legacy */
  options?: IterationFieldOption[];
  /** field only shown for chained steps (stageIndex > 0), per legacy graphql.cursor */
  minStageIndex?: number;
  /** rendered as a JSONPath/XPath "data path" field in legacy */
  isDataPath?: boolean;
}

export interface IterationTypeDef {
  /** iteration.type value persisted to config */
  value: string;
  /** dropdown label (matches Constants.IterationTypes) */
  label: string;
  fields: IterationField[];
}

/**
 * The 11 user-selectable iteration types, in legacy dropdown order
 * (Constants.IterationTypes). Field order matches the legacy stage UI.
 */
export const ITERATION_TYPES: IterationTypeDef[] = [
  {
    value: 'response.id.string',
    label: 'ID (String) In Response',
    fields: [
      { key: 'response.id.field.name', label: 'ID Name', type: 'text', isDataPath: true },
      { key: 'start.id.from', label: 'Start ID from', type: 'text' },
      { key: 'param.id', label: 'URL ID Param', type: 'text' },
      { key: 'end.id.to', label: 'Stop After ID', type: 'text', optional: true },
    ],
  },
  {
    value: 'response.id.number',
    label: 'ID (number) in Response',
    fields: [
      { key: 'response.id.field.name', label: 'ID Name', type: 'text', isDataPath: true },
      { key: 'start.id.from', label: 'Start ID', type: 'text' },
      { key: 'param.id', label: 'URL ID Param', type: 'text' },
      { key: 'param.id.inclusive', label: 'Next ID inclusive?', type: 'checkbox', default: false },
      { key: 'end.id.to', label: 'Stop After ID', type: 'text', optional: true },
    ],
  },
  {
    value: 'paging.next.token',
    label: 'Next Token In Response',
    fields: [
      {
        key: 'response.next.token.data.path',
        label: 'Path to next token',
        type: 'text',
        isDataPath: true,
      },
      { key: 'param.id', label: 'Token param name', type: 'text' },
      { key: 'end.token.to', label: 'Stop After Token', type: 'text', optional: true },
    ],
  },
  {
    value: 'paging.next.url',
    label: 'Next URL in Response',
    fields: [
      {
        key: 'response.next.url.data.path',
        label: 'Path to next URL',
        type: 'text',
        isDataPath: true,
      },
      { key: 'end.url.to', label: 'Stop After URL', type: 'text', optional: true },
    ],
  },
  {
    value: 'link.header',
    label: 'Next URL in Response Header',
    fields: [{ key: 'link', label: 'Response Header containing Next Page URL', type: 'text' }],
  },
  {
    value: 'paging.incrementing.offset',
    label: 'Offset',
    fields: [
      { key: 'param.offset', label: 'URL Param For Offset', type: 'text' },
      { key: 'start.offset.from', label: 'Start From Offset', type: 'text', default: 0 },
      { key: 'param.page.size', label: 'URL Param For Items Per Page', type: 'text' },
      { key: 'page.expected.rows', label: 'Items Per Page', type: 'text' },
      { key: 'end.offset.to', label: 'Stop After Offset', type: 'text', optional: true },
    ],
  },
  {
    value: 'paging.incrementing',
    label: 'Page Number',
    fields: [
      { key: 'param.id', label: 'URL Param For Page No.', type: 'text' },
      { key: 'start.page.from', label: 'Start From Page', type: 'text', default: 1 },
      { key: 'param.page.size', label: 'URL Param For Items Per Page', type: 'text' },
      { key: 'page.expected.rows', label: 'Items Per Page', type: 'text' },
      { key: 'end.page.to', label: 'Stop After Page No.', type: 'text', optional: true },
      {
        key: 'request.parallelism.count',
        label: 'Parallel requests',
        type: 'number',
        default: 1,
      },
    ],
  },
  {
    value: 'graphql.page',
    label: 'GraphQL Page',
    fields: [
      { key: 'graphql.page.name', label: 'Page Variable Name', type: 'text' },
      { key: 'graphql.page.start', label: 'Starting Page Number', type: 'text' },
    ],
  },
  {
    value: 'graphql.cursor',
    label: 'GraphQL Cursor',
    fields: [
      { key: 'graphql.cursor.name', label: 'Cursor Variable Name', type: 'text' },
      {
        key: 'response.graphql.cursor.path',
        label: 'Path to Next Cursor',
        type: 'text',
        isDataPath: true,
      },
      // Only for chained steps (stageIndex > 0) in legacy.
      {
        key: 'graphql.cursor.template',
        label: 'Initial Cursor Source',
        type: 'text',
        minStageIndex: 1,
      },
      {
        key: 'results.pass.through',
        label: 'Include Previous Step Results',
        type: 'checkbox',
        default: false,
        minStageIndex: 1,
      },
    ],
  },
  {
    value: 'async.poll',
    label: 'Asynchronous Job Status API',
    fields: [
      { key: 'async.iteration.path', label: 'Job Status Param', type: 'text' },
      { key: 'async.iteration.value', label: 'Job Completion Value', type: 'text' },
    ],
  },
  {
    value: 'body.as.file',
    label: 'Process API Response as File',
    fields: [
      {
        key: 'file.response.format',
        label: 'Data Format of Response File',
        type: 'select',
        default: 'csv',
        // Hardcoded in legacy ConfigureAPIStage (NOT from backend).
        options: [
          { label: 'CSV', value: 'csv' },
          { label: 'JSON', value: 'json' },
          { label: 'TXT', value: 'txt' },
          { label: 'XML', value: 'xml' },
        ],
      },
    ],
  },
];

/** iteration.type used when a step is not paginated (implicit, not user-selectable). */
export const STATIC_URL_TYPE = 'static.url';
/** iteration.type for data-map/lookup iterations (managed via source-level lookups). */
export const DATA_MAP_TYPE = 'data.map.key.queue';

export const getIterationTypeDef = (type: string): IterationTypeDef | undefined =>
  ITERATION_TYPES.find((t) => t.value === type);

/** Fields visible for a given iteration type at a given stage index. */
export const getVisibleIterationFields = (type: string, stageIndex: number): IterationField[] => {
  const def = getIterationTypeDef(type);
  if (!def) return [];
  return def.fields.filter((f) => f.minStageIndex == null || stageIndex >= f.minStageIndex);
};
